"use client";

import {
  FileText,
  Globe,
  Image,
  MusicNote,
  Paperclip,
  type Icon as PhosphorIcon,
  VideoCamera,
} from "@phosphor-icons/react";
import type { FileUIPart, SourceDocumentUIPart } from "ai";
import type { ComponentProps, HTMLAttributes, ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Icon } from "@/lib/icon";
import { cn } from "@/lib/utils";

// ============================================================================
// Types
// ============================================================================

export type AttachmentData =
  | (FileUIPart & { id: string })
  | (SourceDocumentUIPart & { id: string });

export type AttachmentMediaCategory =
  | "image"
  | "video"
  | "audio"
  | "document"
  | "source"
  | "unknown";

export type AttachmentVariant = "grid" | "inline" | "list";

const mediaCategoryIcons: Record<AttachmentMediaCategory, PhosphorIcon> = {
  audio: MusicNote,
  document: FileText,
  image: Image,
  source: Globe,
  unknown: Paperclip,
  video: VideoCamera,
};

// ============================================================================
// Utility Functions
// ============================================================================

export const getMediaCategory = (
  data: AttachmentData,
): AttachmentMediaCategory => {
  if (data.type === "source-document") {
    return "source";
  }

  const mediaType = data.mediaType ?? "";

  if (mediaType.startsWith("image/")) {
    return "image";
  }
  if (mediaType.startsWith("video/")) {
    return "video";
  }
  if (mediaType.startsWith("audio/")) {
    return "audio";
  }
  if (mediaType.startsWith("application/") || mediaType.startsWith("text/")) {
    return "document";
  }

  return "unknown";
};

export const getAttachmentLabel = (data: AttachmentData): string => {
  if (data.type === "source-document") {
    return data.title || data.filename || "Source";
  }

  const category = getMediaCategory(data);
  return data.filename || (category === "image" ? "Image" : "Attachment");
};

const renderAttachmentImage = (
  url: string,
  filename: string | undefined,
  isGrid: boolean,
) =>
  isGrid ? (
    <img
      alt={filename || "Image"}
      className="size-full object-cover"
      height={96}
      src={url}
      width={96}
    />
  ) : (
    <img
      alt={filename || "Image"}
      className="size-full rounded object-cover"
      height={20}
      src={url}
      width={20}
    />
  );

// ============================================================================
// Contexts
// ============================================================================

interface AttachmentsContextValue {
  variant: AttachmentVariant;
}

const AttachmentsContext = createContext<AttachmentsContextValue | null>(null);

interface AttachmentContextValue {
  data: AttachmentData;
  error: boolean;
  mediaCategory: AttachmentMediaCategory;
  onRemove?: () => void;
  variant: AttachmentVariant;
}

const AttachmentContext = createContext<AttachmentContextValue | null>(null);

// ============================================================================
// Hooks
// ============================================================================

export const useAttachmentsContext = () =>
  useContext(AttachmentsContext) ?? { variant: "grid" as const };

export const useAttachmentContext = () => {
  const ctx = useContext(AttachmentContext);
  if (!ctx) {
    throw new Error("Attachment components must be used within <Attachment>");
  }
  return ctx;
};

// ============================================================================
// Attachments - Container
// ============================================================================

export type AttachmentsProps = HTMLAttributes<HTMLDivElement> & {
  variant?: AttachmentVariant;
};

export const Attachments = ({
  variant = "grid",
  className,
  children,
  ...props
}: AttachmentsProps) => {
  const contextValue = useMemo(() => ({ variant }), [variant]);

  return (
    <AttachmentsContext.Provider value={contextValue}>
      <div
        className={cn(
          "flex items-start",
          variant === "list" ? "flex-col gap-2" : "flex-wrap gap-2",
          variant === "grid" && "ml-auto w-fit",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </AttachmentsContext.Provider>
  );
};

// ============================================================================
// Attachment - Item
// ============================================================================

export type AttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: AttachmentData;
  /**
   * Mark this attachment as failed (upload error, virus scan flag, etc.).
   * Renders the chip with a destructive border + label, sets `aria-invalid`
   * for assistive tech.
   */
  error?: boolean;
  onRemove?: () => void;
};

export const Attachment = ({
  data,
  error = false,
  onRemove,
  className,
  children,
  ...props
}: AttachmentProps) => {
  const { variant } = useAttachmentsContext();
  const mediaCategory = getMediaCategory(data);

  const contextValue = useMemo<AttachmentContextValue>(
    () => ({ data, error, mediaCategory, onRemove, variant }),
    [data, error, mediaCategory, onRemove, variant],
  );

  return (
    <AttachmentContext.Provider value={contextValue}>
      <div
        aria-invalid={error || undefined}
        className={cn(
          "group relative",
          variant === "grid" && [
            "flex size-24 items-center justify-center overflow-hidden",
            "rounded-md border border-border bg-muted",
          ],
          variant === "inline" && [
            "flex select-none items-center gap-2",
            "rounded-md border border-border bg-card p-2",
            "transition-colors hover:bg-accent",
          ],
          variant === "list" && [
            "flex w-full items-center gap-3 rounded-md border border-border bg-card p-3",
            "transition-colors hover:bg-accent",
          ],
          "aria-invalid:border-destructive",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </AttachmentContext.Provider>
  );
};

// ============================================================================
// AttachmentPreview - Media preview
// ============================================================================

export type AttachmentPreviewProps = HTMLAttributes<HTMLDivElement> & {
  fallbackIcon?: ReactNode;
};

export const AttachmentPreview = ({
  fallbackIcon,
  className,
  ...props
}: AttachmentPreviewProps) => {
  const { data, mediaCategory, variant } = useAttachmentContext();

  const iconSize = variant === "grid" ? "size-6" : "size-4";

  const renderIcon = (icon: PhosphorIcon) => (
    <Icon icon={icon} className={cn(iconSize, "text-muted-foreground")} />
  );

  const renderContent = () => {
    if (mediaCategory === "image" && data.type === "file" && data.url) {
      return renderAttachmentImage(data.url, data.filename, variant === "grid");
    }

    if (mediaCategory === "video" && data.type === "file" && data.url) {
      return <video className="size-full object-cover" muted src={data.url} />;
    }

    const icon = mediaCategoryIcons[mediaCategory];
    return fallbackIcon ?? renderIcon(icon);
  };

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden",
        variant === "grid" && "size-full",
        variant === "inline" && "size-10 rounded-sm bg-muted",
        variant === "list" && "size-12 rounded-sm bg-muted",
        className,
      )}
      {...props}
    >
      {renderContent()}
    </div>
  );
};

// ============================================================================
// AttachmentInfo - Name and type display
// ============================================================================

export type AttachmentInfoProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Show the mediaType under the filename. Falls through to nothing if no
   * `size` is provided either. Mutually exclusive with `size` — `size`
   * wins when both are passed.
   */
  showMediaType?: boolean;
  /**
   * Pre-formatted file size to render under the filename (e.g. `"1.2 MB"`).
   * Pre-formatted so the component stays unit-agnostic; format with whatever
   * helper makes sense at the call site (`pretty-bytes`, etc.).
   */
  size?: string;
};

export const AttachmentInfo = ({
  showMediaType = false,
  size,
  className,
  ...props
}: AttachmentInfoProps) => {
  const { data, error, variant } = useAttachmentContext();
  const label = getAttachmentLabel(data);

  if (variant === "grid") {
    return null;
  }

  const meta = size ?? (showMediaType ? data.mediaType : undefined);

  return (
    <div
      className={cn("flex min-w-0 flex-1 flex-col leading-tight", className)}
      {...props}
    >
      <span
        className={cn(
          "truncate font-medium text-sm",
          error ? "text-destructive" : "text-card-foreground",
        )}
      >
        {label}
      </span>
      {meta && (
        <span className="truncate font-normal text-muted-foreground text-xs">
          {meta}
        </span>
      )}
    </div>
  );
};

// ============================================================================
// AttachmentRemove - Remove button
// ============================================================================

export type AttachmentRemoveProps = ComponentProps<typeof Button> & {
  label?: string;
};

export const AttachmentRemove = ({
  label = "Remove",
  className,
  children,
  ...props
}: AttachmentRemoveProps) => {
  const { onRemove, variant } = useAttachmentContext();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove?.();
    },
    [onRemove],
  );

  if (!onRemove) {
    return null;
  }

  return (
    <Button
      aria-label={label}
      className={cn(
        "leading-none",
        variant === "grid" && [
          "absolute top-1 right-1 size-5 rounded-full p-0 text-xs",
          "border border-border bg-card text-muted-foreground",
          "hover:bg-card hover:text-foreground",
        ],
        variant === "inline" && [
          "size-4 shrink-0 rounded-full p-0 text-xs text-muted-foreground",
          "hover:bg-transparent hover:text-foreground",
        ],
        variant === "list" && [
          "size-8 shrink-0 rounded-full p-0 text-base text-muted-foreground",
        ],
        className,
      )}
      onClick={handleClick}
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? (
        <span aria-hidden className="font-normal leading-none">
          ×
        </span>
      )}
      <span className="sr-only">{label}</span>
    </Button>
  );
};

// ============================================================================
// AttachmentHoverCard - Hover preview
// ============================================================================

export type AttachmentHoverCardProps = ComponentProps<typeof HoverCard>;

// Patched from upstream: same Base UI PreviewCard mismatch as in
// prompt-input.tsx — openDelay/closeDelay don't exist on our HoverCard.
export const AttachmentHoverCard = (props: AttachmentHoverCardProps) => (
  <HoverCard {...props} />
);

export type AttachmentHoverCardTriggerProps = ComponentProps<
  typeof HoverCardTrigger
>;

export const AttachmentHoverCardTrigger = (
  props: AttachmentHoverCardTriggerProps,
) => <HoverCardTrigger {...props} />;

export type AttachmentHoverCardContentProps = ComponentProps<
  typeof HoverCardContent
>;

export const AttachmentHoverCardContent = ({
  align = "start",
  className,
  ...props
}: AttachmentHoverCardContentProps) => (
  <HoverCardContent
    align={align}
    className={cn("w-auto p-2", className)}
    {...props}
  />
);

// ============================================================================
// AttachmentEmpty - Empty state
// ============================================================================

export type AttachmentEmptyProps = HTMLAttributes<HTMLDivElement>;

export const AttachmentEmpty = ({
  className,
  children,
  ...props
}: AttachmentEmptyProps) => (
  <div
    className={cn(
      "flex items-center justify-center p-4 text-muted-foreground text-sm",
      className,
    )}
    {...props}
  >
    {children ?? "No attachments"}
  </div>
);
